import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type ActivityList = Awaited<ReturnType<typeof api.get<'/api/v1/activity'>>>;

/* The action types the executor can produce. An unknown one shows its own name rather than a
   missing-translation marker: a new action type must never render as a broken label. */
const ACTION_LABELS: Record<string, string> = {
  archive: 'activity.action.archive',
  mark_read: 'activity.action.mark_read',
  trash: 'activity.action.trash',
  delete: 'activity.action.delete',
  move: 'activity.action.move',
};

/**
 * What Butler did, and the button that takes it back.
 *
 * The point of this screen is that it exists. A product that moves twelve thousand messages on
 * somebody's behalf and offers no list of what it moved is asking for trust it has not earned;
 * the undo being one click away, on every reversible line, is the earning.
 *
 * An undo can take a while — it is a mailbox conversation per message — so the row says it is
 * working rather than freezing the page, and the list is reloaded from the server afterwards
 * rather than patched locally: what happened to the mailbox is the server's account, not ours.
 */
export function ActivityScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [list, setList] = useState<ActivityList | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const result = await api.get('/api/v1/activity', undefined, { tenantId, signal });
        setList(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setList(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const undo = useCallback(
    async (batchId: string) => {
      if (tenantId === undefined) return;
      setUndoing(batchId);
      setNote(null);
      try {
        const result = await api.post(
          '/api/v1/activity/{batchId}/undo',
          {},
          { batchId },
          { tenantId },
        );
        setNote(
          result.status === 'undone'
            ? t('activity.undo.done', { count: result.undoneCount })
            : t('activity.undo.partial', {
                count: result.undoneCount,
                failed: result.failedCount,
              }),
        );
        await load();
      } catch {
        setNote(t('activity.undo.failed'));
      } finally {
        setUndoing(null);
      }
    },
    [tenantId, load, t],
  );

  if (failure !== null && list === null) {
    return (
      <Alert tone="critical" title={t('activity.error.title')}>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (list === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('activity.title')}</h1>
        <p className="greeting__summary">{t('activity.subtitle')}</p>
      </header>

      {note !== null && (
        <p className="mail-message__blocked" role="status">
          {note}
        </p>
      )}

      {list.batches.length === 0 ? (
        <section className="card">
          <p className="empty__body">{t('activity.empty')}</p>
        </section>
      ) : (
        <section className="card">
          <ul className="today-list">
            {list.batches.map((batch) => (
              <li className="today-list__item" key={batch.batchId}>
                <span className={batch.undoneAt !== null ? 'badge' : 'badge badge--ok'}>
                  {batch.undoneAt !== null
                    ? t('activity.status.undone')
                    : ACTION_LABELS[batch.actionType] === undefined
                      ? batch.actionType
                      : t(ACTION_LABELS[batch.actionType]!)}
                </span>
                <span className="today-list__subject">{batch.description}</span>
                <span className="today-list__meta">
                  {[
                    t('activity.counts', {
                      count: batch.succeededCount,
                      total: batch.itemCount,
                    }),
                    batch.failedCount > 0
                      ? t('activity.failed', { count: batch.failedCount })
                      : null,
                    batch.executedAt === null
                      ? null
                      : translator.formatDate(new Date(batch.executedAt)),
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                </span>
                {batch.canUndo ? (
                  <Button disabled={undoing !== null} onClick={() => void undo(batch.batchId)}>
                    {undoing === batch.batchId ? t('activity.undo.working') : t('activity.undo.do')}
                  </Button>
                ) : (
                  /* Said plainly rather than shown as a dead button: the decision queue promised
                     before the fact whether this could be taken back. */
                  <span className="today-list__meta">
                    {batch.undoneAt !== null
                      ? t('activity.undo.already')
                      : t('activity.undo.cannot')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
